import React, { useEffect, useMemo, useRef } from "react";

import { colorsSelector } from "@redux/colorPalette/selectors";
import useSelector from "@redux/useSelector";
import { userTypeSelector } from "@redux/user/selectors";
import { setBackgroundLoaded } from "@redux/videoPlayerResources";
import { backgroundRequestSelector } from "@redux/videoPlayerResources/selectors";
import { RequestState } from "@redux/videoPlayerResources/types";
import { useDispatch } from "react-redux";

import bamDefaultBackground from "assets/background-bam.jpg";
import tslDefaultBackground from "assets/background-tsl.jpg";
import BamNeonLogo from "assets/logo-bam-neon.svg";
import BamOnlineNeonLogo from "assets/logo-bam-online-neon.png";
import { getPixelSizeNumberFromSizingFactor } from "components/b2c/Mp3KbpPlayer/Mp3KbpPlayer.utils";
import { showBamOnlineFeature } from "services/helpers/featureToggle";
import { SCOPE } from "shared-types/constants";
import { B2bUserType } from "shared-types/user";
import {
	BamLogoSmall,
	PlayIconBam,
	PlayNextIconBam,
	TslLogo,
} from "./Background.style";

import {
	B2bSongInfoContainer,
	BackgroundContainer,
	BackgroundImage,
	BamLogo,
	SongDetailsContainer,
	PlayIcon,
	PlayNextIcon,
	SongDetails,
	SongDetailsTitle,
	SongDetailsArtistName,
	SONG_DETAILS_FONT_SIZING_FACTOR,
	SongDetailsSingerName,
} from "./Background.style";
import { getBackgroundFileUrl } from "./Background.utils";
import type { Props } from "./IBackground";
import { FormattedMessage } from "react-intl";
import { Stack } from "@mui/material";

const Background: React.FC<Props> = ({
	colorSetting,
	scope,
	currentSongDetails,
	nextSongDetails,
	videoAreaHeight,
	visible,
}) => {
	const backgroundImageRef = useRef<HTMLImageElement | null>(null);
	const dispatch = useDispatch();
	const backgroundRequest = useSelector(backgroundRequestSelector);
	const colors = useSelector(colorsSelector);
	const userType = useSelector(userTypeSelector);

	useEffect(() => {
		if (!backgroundImageRef.current) {
			return;
		}
		const backgroundImage = backgroundImageRef.current;

		const backgroundLoadListener = () => {
			dispatch(setBackgroundLoaded());
		};
		backgroundImage.addEventListener("load", backgroundLoadListener);
		backgroundImage.src = getBackgroundFileUrl(userType, colorSetting);

		return () => {
			backgroundImage.removeEventListener("load", backgroundLoadListener);
		};
	}, [backgroundImageRef, colorSetting, dispatch, userType, userType]);

	const songDetailsFontSizeInPx = useMemo(
		() =>
			getPixelSizeNumberFromSizingFactor(
				videoAreaHeight,
				SONG_DETAILS_FONT_SIZING_FACTOR,
			),
		[videoAreaHeight],
	);

	const setDefaultImage = () => {
		if (!backgroundImageRef.current) {
			return;
		}
		if (userType === B2bUserType.BAM) {
			backgroundImageRef.current.src = bamDefaultBackground;
		}
		if (userType === B2bUserType.TSL) {
			backgroundImageRef.current.src = tslDefaultBackground;
		}
	};

	const CurrentSongInfos = () => {
		if (!currentSongDetails) {
			return null;
		}

		return (
			<Stack direction="column" gap={1}>
				{userType === B2bUserType.BAM ? (
					<Stack direction="row" gap={2} alignItems="center">
						<PlayIconBam />
						<div
							style={{
								fontFamily: "RailwayGank",
								fontSize: "60px",
								color: "#E32084",
								textTransform: "uppercase",
							}}
						>
							<FormattedMessage id={"song.in-progress"} />
						</div>
					</Stack>
				) : (
					<PlayIcon />
				)}

				<SongDetails>
					{currentSongDetails.singerName && (
						<SongDetailsSingerName>
							<FormattedMessage id={"song.singer"} />{" "}
							{currentSongDetails.singerName}
						</SongDetailsSingerName>
					)}
					<SongDetailsTitle>{currentSongDetails.title}</SongDetailsTitle>
					<SongDetailsArtistName>
						{currentSongDetails.artistName}
					</SongDetailsArtistName>
				</SongDetails>
			</Stack>
		);
	};

	const NextSongInfos = () => {
		if (!nextSongDetails) {
			return null;
		}

		return (
			<Stack direction="column" gap={1}>
				{userType === B2bUserType.BAM ? (
					<Stack direction="row" gap={2} alignItems="center">
						<PlayNextIconBam />
						<div
							style={{
								fontFamily: "RailwayGank",
								fontSize: "60px",
								color: "#E32084",
								textTransform: "uppercase",
							}}
						>
							<FormattedMessage id={"song.next"} />
						</div>
					</Stack>
				) : (
					<PlayNextIcon />
				)}
				<SongDetails>
					{nextSongDetails.singerName && (
						<SongDetailsSingerName>
							<FormattedMessage id={"song.singer"} />{" "}
							{nextSongDetails.singerName}
						</SongDetailsSingerName>
					)}
					<SongDetailsTitle>{nextSongDetails.title}</SongDetailsTitle>
					<SongDetailsArtistName>
						{nextSongDetails.artistName}
					</SongDetailsArtistName>
				</SongDetails>
			</Stack>
		);
	};

	return (
		<BackgroundContainer visible={visible}>
			<BackgroundImage
				ref={backgroundImageRef}
				alt="background"
				onError={setDefaultImage}
			/>
			{backgroundRequest.state === RequestState.success &&
				(scope === SCOPE.B2C ? (
					<BamLogo
						src={showBamOnlineFeature() ? BamOnlineNeonLogo : BamNeonLogo}
					/>
				) : (
					<B2bSongInfoContainer {...{ songDetailsFontSizeInPx, colors }}>
						<SongDetailsContainer>
							<CurrentSongInfos />
						</SongDetailsContainer>
						{userType === B2bUserType.BAM ? <BamLogoSmall /> : <TslLogo />}
						<SongDetailsContainer right>
							<NextSongInfos />
						</SongDetailsContainer>
					</B2bSongInfoContainer>
				))}
		</BackgroundContainer>
	);
};

export default React.memo(Background);
